import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { App, Alert, Button, Card, ColorPicker, Divider, Flex, Input, Segmented, Select, Spin, Typography, Upload } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAlignCenter, faAlignLeft, faCopy, faLink, faRotateLeft, faUpload } from '@fortawesome/free-solid-svg-icons';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { EXTERNAL_FORM_DEFAULTS, EXTERNAL_FORM_FONTS, isExternalFormValid, isHexColor } from '@/features/externalForm/constants';
import { ExternalFormPreview } from '@/features/externalForm/components/ExternalFormPreview';
import {
  externalFormQueryKeys,
  useDeleteExternalFormLogo,
  useGenerateExternalFormLink,
  useGetExternalForm,
  useUpdateExternalForm,
  useUploadExternalFormLogo,
} from '@/features/externalForm/hooks/useExternalForm';

const { Text } = Typography;

const LOGO_EXTENSION = /\.(jpe?g|png|svg)$/i;
const LOGO_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml'];
const LOGO_ACCEPT = '.jpg,.jpeg,.png,.svg,image/jpeg,image/png,image/svg+xml';

const isAllowedLogo = (file) => LOGO_TYPES.includes(file.type) || LOGO_EXTENSION.test(file.name);

const fileNameFromProxy = (logo) => {
  const token = logo.split('?')[0].replace(/\/$/, '').split('/').pop();
  const payload = token.split('.')[1];
  if (!payload) return '';
  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const data = JSON.parse(window.atob(padded));
    const name = String(data.file_name || '').split('/').pop();
    return name ? decodeURIComponent(name) : '';
  } catch (error) {
    return '';
  }
};

const logoFileName = (logo, pendingFile) => {
  if (pendingFile?.name) return pendingFile.name;
  if (!logo) return '';
  if (logo.includes('/api/file/')) return fileNameFromProxy(logo);
  const path = logo.split('?')[0].replace(/\/$/, '').split('/').pop();
  try {
    return decodeURIComponent(path);
  } catch (error) {
    return path;
  }
};

const SectionLabel = ({ children }) => (
  <Text style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', color: '#5b6472' }}>{children}</Text>
);

const ColorField = ({ label, value, onChange }) => {
  const [text, setText] = useState(value);

  useEffect(() => {
    setText(value);
  }, [value]);

  return (
    <Flex vertical gap={4}>
      <Text strong>{label}</Text>
      <Flex gap={8} align='center'>
        <ColorPicker
          value={value}
          format='hex'
          disabledAlpha
          onChange={(color) => {
            const hex = color.toHexString().slice(0, 7).toUpperCase();
            if (isHexColor(hex)) onChange(hex);
          }}
        />
        <Input
          value={text}
          maxLength={7}
          onChange={(event) => {
            const next = event.target.value.toUpperCase();
            setText(next);
            if (isHexColor(next)) onChange(next);
          }}
          onBlur={() => setText(value)}
          style={{ width: 110 }}
        />
      </Flex>
    </Flex>
  );
};

const copyWithTextarea = (value) => {
  if (!value) return false;
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch (error) {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

const copyText = async (value) => {
  if (!value) return false;
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (error) {
      return copyWithTextarea(value);
    }
  }
  return copyWithTextarea(value);
};

const copyWhenReady = (textPromise) => {
  if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
    const item = new ClipboardItem({
      'text/plain': textPromise.then((text) => {
        if (!text) throw new Error('empty link');
        return new Blob([text], { type: 'text/plain' });
      }),
    });
    return navigator.clipboard.write([item]).then(() => true).catch(() => false);
  }
  return textPromise
    .then((text) => copyWithTextarea(text))
    .catch(() => false);
};

export const ExternalFormEditor = () => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const query = useGetExternalForm();
  const updateMutation = useUpdateExternalForm();
  const uploadLogoMutation = useUploadExternalFormLogo();
  const deleteLogoMutation = useDeleteExternalFormLogo();
  const generateLinkMutation = useGenerateExternalFormLink();

  const [draft, setDraft] = useState(null);
  const [link, setLink] = useState(null);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const objectUrlRef = useRef(null);

  const revokeObjectUrl = () => {
    if (!objectUrlRef.current) return;
    URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = null;
  };

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  useEffect(() => {
    if (!query.data || draft) return;
    setDraft(query.data.form);
    setLink(query.data.link);
  }, [query.data, draft]);

  const notifyError = (error) => {
    notification.error({ message: getErrorMessage(error) });
  };

  const updateDraft = (patch) => {
    setDraft((current) => ({ ...current, ...patch }));
  };

  const handleLogo = (file) => {
    if (!isAllowedLogo(file)) {
      notification.error({ message: 'El logo debe ser JPG, PNG o SVG.' });
      return;
    }
    revokeObjectUrl();
    const localUrl = URL.createObjectURL(file);
    objectUrlRef.current = localUrl;
    setPendingLogoFile(file);
    setRemoveLogo(false);
    updateDraft({ logo: localUrl });
  };

  const handleRemoveLogo = () => {
    revokeObjectUrl();
    setPendingLogoFile(null);
    setRemoveLogo(true);
    updateDraft({ logo: null });
  };

  const handleReset = () => {
    revokeObjectUrl();
    setPendingLogoFile(null);
    setRemoveLogo(true);
    setDraft({ ...EXTERNAL_FORM_DEFAULTS });
  };

  const handleSave = async () => {
    if (!draft || !isExternalFormValid(draft)) {
      notification.error({ message: 'Revisa el título, el subtítulo, la tipografía y los colores antes de guardar.' });
      return;
    }

    try {
      let saved = await updateMutation.mutateAsync(draft);
      if (pendingLogoFile) {
        saved = await uploadLogoMutation.mutateAsync(pendingLogoFile);
        setPendingLogoFile(null);
        setRemoveLogo(false);
      } else if (removeLogo) {
        saved = await deleteLogoMutation.mutateAsync();
        setRemoveLogo(false);
      }
      setDraft(saved);
      queryClient.setQueryData(externalFormQueryKeys.detail(), (current) => ({
        ...(current || {}),
        form: saved,
        link: link ?? current?.link ?? null,
      }));
      notification.success({ message: 'Formulario guardado.' });
    } catch (error) {
      notifyError(error);
    }
  };

  const handleCopy = async (url) => {
    const copied = await copyText(url);
    if (copied) {
      notification.success({ message: 'Link copiado.' });
      return;
    }
    notification.error({ message: 'No se pudo copiar el link. Cópialo desde el campo.' });
  };

  const handleGenerateLink = async () => {
    const pending = generateLinkMutation.mutateAsync();
    const copyPromise = copyWhenReady(pending.then((generated) => generated.url));
    try {
      const generated = await pending;
      setLink(generated);
      let copied = await copyPromise;
      if (!copied) copied = copyWithTextarea(generated.url);
      notification.success({
        message: copied
          ? 'Link generado y copiado.'
          : 'Link generado. Cópialo desde el campo de abajo.',
      });
    } catch (error) {
      notifyError(error);
    }
  };

  if (query.isError && !draft) {
    return (
      <Alert
        type='error'
        showIcon
        message={getErrorMessage(query.error)}
        action={<Button onClick={() => query.refetch()}>Reintentar</Button>}
      />
    );
  }

  if (query.isLoading || !draft) {
    return <Spin />;
  }

  const saving = updateMutation.isPending || uploadLogoMutation.isPending || deleteLogoMutation.isPending;
  const currentLogoName = logoFileName(draft.logo, pendingLogoFile);

  return (
    <Flex gap={24} align='flex-start' wrap='wrap'>
      <Card style={{ width: 320, flex: '1 1 280px', maxWidth: 360 }} styles={{ body: { padding: 20 } }}>
        <Flex vertical gap={16}>
          <Flex vertical gap={12}>
            <SectionLabel>LOGO</SectionLabel>
            <Upload.Dragger
              accept={LOGO_ACCEPT}
              showUploadList={false}
              multiple={false}
              beforeUpload={(file) => {
                handleLogo(file);
                return false;
              }}
              style={{ background: '#f7f8fb' }}
            >
              <Flex vertical align='center' gap={4}>
                <FontAwesomeIcon icon={faUpload} style={{ color: '#8b95a7' }} />
                <Text>{currentLogoName || 'Arrastra el logo aquí'}</Text>
                <Text type='secondary' style={{ fontSize: 12 }}>JPG, PNG o SVG</Text>
              </Flex>
            </Upload.Dragger>
            {draft.logo && (
              <Flex gap={8} align='center'>
                <Upload
                  accept={LOGO_ACCEPT}
                  showUploadList={false}
                  multiple={false}
                  beforeUpload={(file) => {
                    handleLogo(file);
                    return false;
                  }}
                >
                  <Button icon={<FontAwesomeIcon icon={faUpload} />}>Reemplazar</Button>
                </Upload>
                <Button type='text' onClick={handleRemoveLogo}>Quitar</Button>
              </Flex>
            )}
            <Flex vertical gap={4}>
              <Text strong>Posición del logo</Text>
              <Segmented
                block
                value={draft.logoPosition}
                onChange={(logoPosition) => updateDraft({ logoPosition })}
                options={[
                  {
                    label: (
                      <span>
                        <FontAwesomeIcon icon={faAlignLeft} />
                        {' '}
                        Izquierda
                      </span>
                    ),
                    value: 'left',
                  },
                  {
                    label: (
                      <span>
                        <FontAwesomeIcon icon={faAlignCenter} />
                        {' '}
                        Centrado
                      </span>
                    ),
                    value: 'center',
                  },
                ]}
              />
            </Flex>
          </Flex>

          <Divider style={{ margin: 0 }} />

          <Flex vertical gap={12}>
            <SectionLabel>TEXTOS</SectionLabel>
            <Flex vertical gap={4}>
              <Text strong>Título del formulario</Text>
              <Input
                value={draft.title}
                maxLength={120}
                onChange={(event) => updateDraft({ title: event.target.value })}
              />
            </Flex>
            <Flex vertical gap={4}>
              <Text strong>Subtítulo</Text>
              <Input
                value={draft.subtitle}
                maxLength={200}
                onChange={(event) => updateDraft({ subtitle: event.target.value })}
              />
            </Flex>
          </Flex>

          <Divider style={{ margin: 0 }} />

          <Flex vertical gap={12}>
            <SectionLabel>APARIENCIA</SectionLabel>
            <Flex vertical gap={4}>
              <Text strong>Tipografía</Text>
              <Select
                value={draft.fontFamily}
                onChange={(fontFamily) => updateDraft({ fontFamily })}
                options={EXTERNAL_FORM_FONTS.map((font) => ({
                  value: font,
                  label: <span style={{ fontFamily: `'${font}', sans-serif` }}>{font}</span>,
                }))}
              />
            </Flex>
            <ColorField
              label='Color principal (títulos y botones)'
              value={draft.primaryColor}
              onChange={(primaryColor) => updateDraft({ primaryColor })}
            />
            <ColorField
              label='Color de la letra'
              value={draft.textColor}
              onChange={(textColor) => updateDraft({ textColor })}
            />
            <Button type='link' icon={<FontAwesomeIcon icon={faRotateLeft} />} onClick={handleReset} style={{ padding: 0, width: 'fit-content' }}>
              Restablecer
            </Button>
          </Flex>

          <Button type='primary' onClick={handleSave} loading={saving} block>
            Guardar
          </Button>

          {link?.url ? (
            <Flex vertical gap={8}>
              <Input.TextArea value={link.url} readOnly autoSize={{ minRows: 2, maxRows: 4 }} />
              <Button icon={<FontAwesomeIcon icon={faCopy} />} onClick={() => handleCopy(link.url)}>
                Copiar link
              </Button>
            </Flex>
          ) : (
            <Button
              type='primary'
              icon={<FontAwesomeIcon icon={faLink} />}
              onClick={handleGenerateLink}
              loading={generateLinkMutation.isPending}
              block
            >
              Generar link del formulario
            </Button>
          )}
        </Flex>
      </Card>

      <div style={{ flex: '1 1 480px', minWidth: 0 }}>
        <ExternalFormPreview form={draft} />
      </div>
    </Flex>
  );
};
